import React, { forwardRef, useEffect, useRef } from 'react'
import { Box, Flex, FormControl, FormLabel } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import { TextInput } from 'components/ui'
import { TaskInput } from 'components/TasksV2/UI/TaskInput'
import useLocaleText from 'components/useLocaleText'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'

interface ITaskTitleProps {
  inputElement?: React.ReactNode
  textAlign?: React.CSSProperties['textAlign']
  formControlWidth?: string
  inputBoxShadow?: string
  showLabel?: boolean
  label?: React.ReactNode
  defaultValue?: string
}

const TaskTitle = forwardRef<HTMLDivElement, ITaskTitleProps>(
  (
    {
      inputElement,
      textAlign = 'center',
      formControlWidth = '50%',
      inputBoxShadow,
      showLabel = false,
      label,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const { errors, control, setValue } = useFormContext()
    const t_task_name_error = useLocaleText('t_task_name_error')
    const t_task_title = useLocaleText('t_task_title')
    const t_task_name = useLocaleText('create_task_name_title')
    const initialValue = useRef(defaultValue)

    useEffect(() => {
      // RHF v5 reuses form defaults on remount. Restore the current snapshot when the
      // onboarding field moves to the panel header, rather than the original task name.
      if (initialValue.current !== undefined) setValue('title', initialValue.current)
    }, [setValue])

    return (
      <Flex w="100%" justifyContent="center" ref={typeof ref === 'function' ? ref : undefined} {...props}>
        <FormControl mb={4} w={formControlWidth} isRequired isInvalid={!!errors.title}>
          {showLabel && (
            <FormLabel htmlFor="task-creation-title" fontSize="13px" fontWeight={600} mb="8px" display="block">
              {label ?? t_task_name}
            </FormLabel>
          )}
          <Controller
            id={showLabel ? 'task-creation-title' : undefined}
            name="title"
            control={control}
            {...(defaultValue !== undefined ? { defaultValue } : {})}
            rules={{
              required: t_task_name_error,
              maxLength: { value: 100, message: t_task_name_error },
              validate: (value) => value.trim() !== '' || t_task_name_error,
            }}
            as={showLabel ? TextInput : TaskInput}
            isInvalid={!!errors.title}
            {...(!showLabel ? { isError: !!errors.title } : {})}
            placeholder={t_task_title}
            maxLength={100}
            {...(showLabel ? { elementHeight: '40px' } : { border: 'none' })}
            textAlign={textAlign}
            {...(!showLabel ? { inputElement } : {})}
            {...(inputBoxShadow !== undefined ? { boxShadow: inputBoxShadow } : {})}
          />
          {errors.title && (
            <Box px="10px" my="5px">
              <ErrorMessage errorMessage={t_task_name_error} />
            </Box>
          )}
        </FormControl>
      </Flex>
    )
  }
)

export default TaskTitle
